import DropdownSelect from "#components/DropdownSelect/index.js";
import PropertyValueInput from "#components/PropertyValueInput/index.js";
import SelectionGrid from "#components/SelectionGrid/index.js";
import TranslatableTextField from "#components/TranslatableTextField/index.js";
import { Box, Grid, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "components/LoadingButton";
import Dialog from "components/NoScrollDialog";
import { useLanguage } from "contexts/language.context";
import { DataContainer } from "layouts/logsheets/entities/AllEntities/EntitiesDialog";
import CollapsibleSection from "layouts/logsheets/templates/components/CollapsibleSection";
import * as React from "react";
import { useEffect } from "react";
import entitiesService from "services/entities-service";
import propertiesService from "services/properties-service";

export default function EntityElementsDialog({
  isOpen,
  onSubmit,
  data,
  mode,
  onClose,
}) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [updates, setUpdates] = React.useState({});
  const [initialEntityElement, setInitialEntityElement] = React.useState({
    name: { en: "" },
    referenceCode: "",
  });
  const [options, setOptions] = React.useState({
    accounts: [],
    entities: [],
    properties: [],
  });
  const { label } = useLanguage();
  const [error, setError] = React.useState(null);

  const entity = React.useMemo(() => {
    const selectedId = updates.entityId || initialEntityElement.entityId;
    return options.entities.find((e) => e.id == selectedId);
  }, [initialEntityElement.entityId, updates.entityId, options.entities]);

  const entityElement = React.useMemo(() => {
    let initialProperties =
      updates?.properties || initialEntityElement?.entityElementSheetProperties;

    console.log("initialProperties: ", initialProperties);

    let properties =
      entity?.entitySheetProperties?.map((entitySheetProperty) => {
        const found = initialProperties?.find(
          (p) => p.entitySheetPropertyId == entitySheetProperty.id,
        );

        return {
          ...entitySheetProperty,
          ...found,
          entitySheetPropertyId: entitySheetProperty.id,
          id: found?.id || "temp-" + entitySheetProperty.id,
          sheetRule: {
            ...entitySheetProperty?.sheetRule,
            ...found?.sheetRule,
            id: found?.sheetRule?.id || Date.now() * Math.random(),
          },
        };
      }) || [];

    return {
      ...initialEntityElement,
      ...updates,
      properties,
    };
  }, [initialEntityElement, updates, updates?.properties, entity]);

  console.log("entityElement: ", entityElement, updates);

  useEffect(() => {
    setUpdates({});
  }, [initialEntityElement?.id]);

  async function fetch() {
    let [ok, data] = await entitiesService.getEntities();
    if (ok) {
      setOptions((options) => ({ ...options, entities: data }));
    }

    [ok, data] = await propertiesService.getProperties();
    if (ok) {
      setOptions((options) => ({ ...options, properties: data }));
    }
  }

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
  }, [isOpen, mode]);

  React.useEffect(() => {
    if (data && mode == "edit")
      setInitialEntityElement({ ...data, name: data?.name });
    else
      setInitialEntityElement({
        name: "",
      });

    console.log("DATA: ", data, mode);
  }, [data, mode, isOpen]);

  React.useEffect(() => {
    setError(null);
    if (isOpen) fetch();
  }, [mode, isOpen]);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const dropdownOptions = React.useMemo(() => {
    return {
      entities:
        options.entities?.map((v) => ({ id: v.id, label: v.name })) || [],
      properties:
        options.properties?.map((v) => ({ id: v.id, label: v.name })) || [],
    };
  }, [options]);

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          const result = await onSubmit(entityElement);

          if (result.error) {
            setError(result.error);
          } else {
            setError(null);
            handleClose();
          }

          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        {mode == "edit"
          ? label("edit-entity-element")
          : label("create-entity-element")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <CollapsibleSection label="Info">
            <Grid item xs={6}>
              <TranslatableTextField
                autoFocus
                required
                id="name"
                name="name"
                label={label("name-singular")}
                type="text"
                values={entityElement.name}
                onChange={(e) =>
                  setUpdates((prev) => ({ ...prev, name: e.target.value }))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <DropdownSelect
                required
                label={"Entity"}
                initialValue={entityElement.entityId}
                items={dropdownOptions.entities}
                onChange={(value) => {
                  setUpdates((prev) => ({
                    ...prev,
                    entityId: value?.id || "",
                  }));
                }}
                fullWidth
              />
            </Grid>
            <SelectionGrid
              level={["account", "branchGroup", "branch"]}
              accountId={entityElement.accountId}
              setAccountId={(accountId) => {
                setUpdates((prev) => ({ ...prev, accountId }));
              }}
              branchGroupId={entityElement.branchGroupId}
              setBranchGroupId={(branchGroupId) => {
                setUpdates((prev) => ({ ...prev, branchGroupId }));
              }}
              branchId={entityElement.branchId}
              setBranchId={(branchId) => {
                setUpdates((prev) => ({ ...prev, branchId }));
              }}
              xs={4}
            />
            <Grid item xs={6}>
              <TextField
                label={label("reference-code-singular")}
                fullWidth
                value={entityElement.referenceCode || ""}
                onChange={(e) => {
                  setUpdates((prev) => ({
                    ...prev,
                    referenceCode: e.target.value,
                  }));
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={"Serial number"}
                fullWidth
                value={entityElement.serialNumber || ""}
                onChange={(e) => {
                  setUpdates((prev) => ({
                    ...prev,
                    serialNumber: e.target.value,
                  }));
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <PropertyValueInput
                label={"Attributes"}
                value={entityElement.attributes || ""}
                onChange={(e) => {
                  setUpdates((prev) => ({
                    ...prev,
                    attributes: e,
                  }));
                }}
              />
            </Grid>
          </CollapsibleSection>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              ml: 2,
              mt: 2,
            }}
          >
            <DataContainer
              updateRulesOnly
              properties={entityElement.properties || []}
              onPropertiesChange={(newProperties) => {
                console.log("New properties: ", newProperties);
                setUpdates((u) => ({ ...u, properties: newProperties }));
              }}
              options={options}
              dropdownOptions={dropdownOptions}
              setOptions={setOptions}
            />
          </Box>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {mode == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
